import React, { useEffect, useState } from 'react';

import GoogleMapsDotIcon from 'assets/icons/googleMapsDot.svg';
import LibaryBooksIcon from 'assets/icons/library-books.svg';

import { ERoute } from 'types/enums/ERoute';
import { EUrlSearchParam } from 'types/enums/EUrlSearchParam';
import {
  IConsultantCompany,
  IConsultantDetails,
  IConsultantResponse,
  IUser,
} from 'types/interfaces/sulu/IUser';

import { calculateConsultantDistance } from 'utils/calculateDistance';
import createFocalPoints from 'utils/createFocalPoints';
import currentLanguage from 'utils/sulu/currentLanguage';
import getAllConsultantArticles from 'utils/sulu/getAllConsultantArticles';
import getConsultant from 'utils/sulu/getConsultant';

import getDefaultProfileThumbnail from '../../../utils/getDefaultProfileThumbnail';
import getAllConsultants from '../../../utils/sulu/getAllConsultants';
import getUserLocation from '../../../utils/sulu/getUserLocation';

export interface IConsultantTile {
  id: number;
  name: string;
  url: string;
}

const ConsultantTile = ({ id, name, url }: IConsultantTile) => {
  const [consultantProfile, setConsultantProfile] = useState<IUser | null>(
    null,
  );
  const [consultantDistance, setConsultantDistance] = useState<string>('');
  const [company, setCompany] = useState<IConsultantCompany | undefined>(
    undefined,
  );
  const [hasArticle, setHasArticle] = useState<boolean>(false);
  const [consultantDetails, setConsultantDetails] = useState<
    IConsultantDetails | undefined
  >(undefined);
  const [avatar, setAvatar] = useState<string | null>(null);

  const defaultProfileThumbnail = getDefaultProfileThumbnail();

  getAllConsultantArticles(id, 1).then(articles => {
    if (articles && articles.length > 0) {
      setHasArticle(true);
    }
  });

  useEffect(() => {
    getConsultant(id).then(profile => {
      setConsultantProfile(profile);
    });
  }, [id]);

  useEffect(() => {
    const cDetails: IConsultantDetails | undefined = consultantProfile
      ? consultantProfile.consultantDetails
      : undefined;
    setConsultantDetails(cDetails);
    setCompany(cDetails ? cDetails.company : undefined);
  }, [consultantProfile, company]);

  useEffect(() => {
    const cDetails: IConsultantDetails | undefined = consultantProfile
      ? consultantProfile.consultantDetails
      : undefined;
    setConsultantDetails(cDetails);
    setCompany(cDetails ? cDetails.company : undefined);

    if (!consultantProfile || !company) {
      return;
    }

    getUserLocation().then(userLocation => {
      if (!userLocation) {
        return;
      }
      const consultantAddress = `${company.lat}, ${company.lon}`;
      calculateConsultantDistance(userLocation, consultantAddress).then(
        distance => {
          if (distance) {
            const formattedDistance = distance.toString().replace('.', ',');
            setConsultantDistance(formattedDistance);
          } else {
            setConsultantDistance('');
          }
        },
      );
    });
  }, [consultantProfile, company]);

  useEffect(() => {
    getAllConsultants().then((consultants: IConsultantResponse[]) => {
      const foundConsultant = consultants?.find(
        consultant => consultant.id === id,
      );
      if (foundConsultant) {
        const updatedAvatar = foundConsultant.avatar.replace(
          '/1200x627/',
          '/sulu-400x400/',
        );
        setAvatar(updatedAvatar);
      }
    });
  }, []);

  return (
    <a
      className='md:flex gap-4 rounded-xl border-2 hover:text-inherit'
      href={url}
    >
      <div className='min-h-[20rem] bg-blue-light p-4 rounded-lg flex justify-center items-center'>
        <div
          className={`${
            avatar ? 'h-72 md:w-60 md:h-full w-full' : 'h-60 w-60'
          } bg-center bg-no-repeat bg-cover rounded-xl`}
          style={{
            backgroundImage: avatar
              ? `url(${avatar})`
              : `url(${defaultProfileThumbnail})`,
          }}
        />
      </div>

      <div className='md:basis-3/4 p-4 flex flex-col'>
        <div className='h-8'>
          {consultantDistance && (
            <div className='flex flex-wrap md:justify-end'>
              <GoogleMapsDotIcon className='icon mt-1' />
              <span>{consultantDistance} km von mir entfernt</span>
            </div>
          )}
        </div>

        <div className='md:flex md:justify-between items-center'>
          <h2 className='cursor-pointer'>{name}</h2>
        </div>
        {company && (
          <span className='mt-2'>
            {`${company.plz} ${company.ort}; Tel: ${company.telephone}${
              consultantProfile ? `; E-Mail: ${consultantProfile.email}` : ''
            }`}
          </span>
        )}

        <div className='grid grid-cols-1  gap-8 lg:grid-cols-2'>
          <div className='md:col-span-1 mt-4'>
            {company && <h3>{company.companyName}</h3>}
            <div className='mt-4 overflow-y-auto max-h-[7rem] pr-4'>
              {consultantDetails?.myOffer ? (
                <p className='text-justify '>
                  {consultantDetails.myOffer || ''}
                </p>
              ) : (
                <p className='text-black/50'>Keine Beschreibung angegeben</p>
              )}
            </div>
          </div>
          <div className='md:col-span-1 my-4'>
            {consultantDetails ? (
              <div>
                <h3>Tätigkeit</h3>
                <div className='mt-4 overflow-y-auto  max-h-[7rem] pr-4 overflow-x-hidden'>
                  {createFocalPoints(consultantDetails.focalPoints)}
                </div>
              </div>
            ) : (
              <p className='text-black/50'>
                Keine Tätigkeiten & Schwerpunkte angegeben
              </p>
            )}
          </div>
        </div>
        {hasArticle && (
          <div className='flex flex-row mt-6'>
            <LibaryBooksIcon className='icon fill-orange mr-2' />
            <a
              href={`/${currentLanguage}/${ERoute.CONSULTANT_PROFILE}?${EUrlSearchParam.CONSULTANT_ID}=${id}#myArctiles`}
            >
              Zu meinen Artikeln
            </a>
          </div>
        )}
      </div>
    </a>
  );
};

export default ConsultantTile;
